import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useNavigate } from "react-router-dom";
import { Check, Zap, Crown, Star, Flame } from "lucide-react";
import MarketingHeader from "@/components/MarketingHeader";
import MarketingFooter from "@/components/MarketingFooter";
import { trackEvent } from "@/lib/analytics";
import { STRIPE_PRICES } from "@/config/stripe";

const Pricing = () => {
  const navigate = useNavigate();
  const [selectedBilling, setSelectedBilling] = useState<'monthly' | 'quarterly' | 'annual'>('annual');

  useEffect(() => {
    trackEvent('pricing_page_viewed');
  }, []);

  const freeFeatures = [
    "Preset study timers (30/60 min)",
    "8 hours daily study limit",
    "Global & friends leaderboards",
    "XP & prestige progression",
    "Basic analytics",
    "Coin economy",
    "Achievement badges",
    "Break recommendations"
  ];

  const premiumFeatures = [
    "Everything in Free, plus:",
    "Unlimited custom timers (15–180 min)",
    "16 hours daily study limit",
    "2 streak freeze days/month",
    "2 break skip passes/week",
    "Advanced analytics (heatmaps, trends)",
    "Premium themes",
    "Priority support",
    "Early access to new features"
  ];

  const billingOptions = [
    { key: 'monthly' as const, ...STRIPE_PRICES.monthly },
    { key: 'quarterly' as const, ...STRIPE_PRICES.quarterly },
    { key: 'annual' as const, ...STRIPE_PRICES.annual },
  ];

  const selectedPrice = STRIPE_PRICES[selectedBilling];
  const monthlyEquivalent = selectedBilling === 'monthly' 
    ? selectedPrice.amount 
    : selectedBilling === 'quarterly'
      ? (selectedPrice.amount / 3).toFixed(2)
      : (selectedPrice.amount / 12).toFixed(2);

  return (
    <div className="min-h-screen bg-background charcoal-pro">
      <MarketingHeader />

      {/* Hero */}
      <section className="container mx-auto px-4 py-12 md:py-16 text-center">
        <p className="text-lg text-primary font-medium tracking-wide mb-4">
          Made for consistency. Built for enjoyment.
        </p>
        <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-4">
          Remove the Limits. Stay Consistent.
        </h1>
        <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto">
          Free gets you started. Premium unlocks the tools for serious, sustainable study.
        </p>
      </section>

      {/* Pricing Cards */}
      <section className="container mx-auto px-4 py-12">
        <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
          {/* Free Tier */}
          <Card className="p-8 relative">
            <div className="text-center mb-6">
              <Zap className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
              <h3 className="text-2xl font-bold text-foreground mb-2">Free</h3>
              <p className="text-muted-foreground text-sm mb-4">
                Try the system. Build the habit.
              </p>
              <div className="flex items-baseline justify-center gap-2">
                <span className="text-5xl font-bold text-foreground">$0</span>
                <span className="text-muted-foreground">/ forever</span>
              </div>
            </div>

            <Button 
              className="w-full mb-6" 
              size="lg"
              variant="outline"
              onClick={() => {
                trackEvent('pricing_plan_clicked', { plan: 'free' });
                navigate('/auth');
              }}
            >
              Get Started Free
            </Button>

            <ul className="space-y-3">
              {freeFeatures.map((feature, idx) => (
                <li key={idx} className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-muted-foreground">{feature}</span>
                </li>
              ))}
            </ul>
          </Card>

          {/* Premium Tier */}
          <Card className="p-8 relative border-primary shadow-xl scale-105">
            {/* Badge */}
            <div className="absolute -top-3 left-1/2 -translate-x-1/2">
              <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-primary text-primary-foreground text-sm font-medium">
                <Flame className="w-3 h-3" />
                Best Value
              </span>
            </div>
            
            <div className="text-center mb-6">
              <Crown className="w-12 h-12 mx-auto mb-4 text-primary" />
              <h3 className="text-2xl font-bold text-foreground mb-2">Premium</h3>
              <p className="text-muted-foreground text-sm mb-4">
                Consistency without limits. Enjoyment without interruption.
              </p>
              
              {/* Billing Toggle */}
              <div className="flex justify-center gap-2 mb-4">
                {billingOptions.map((option) => (
                  <button
                    key={option.key}
                    onClick={() => setSelectedBilling(option.key)}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all relative ${
                      selectedBilling === option.key
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-secondary text-secondary-foreground hover:bg-secondary/80'
                    }`}
                  >
                    {option.displayName}
                    {option.popular && (
                      <span className="absolute -top-2 -right-2 flex h-4 w-4 items-center justify-center">
                        <Star className="w-3 h-3 text-primary fill-primary" />
                      </span>
                    )}
                  </button>
                ))}
              </div>

              <div className="flex items-baseline justify-center gap-2">
                <span className="text-5xl font-bold text-foreground">${selectedPrice.amount}</span>
                <span className="text-muted-foreground">
                  / {selectedBilling === 'monthly' ? 'month' : selectedBilling === 'quarterly' ? '3 months' : 'year'}
                </span>
              </div>
              
              <div className="mt-2 space-y-1">
                {selectedBilling !== 'monthly' && (
                  <p className="text-sm text-primary font-medium">
                    ${monthlyEquivalent}/month • {selectedPrice.savings}
                  </p>
                )}
                {selectedPrice.savings && (
                  <Badge variant="secondary" className="text-xs">
                    {selectedPrice.savings}
                  </Badge>
                )}
              </div>
            </div>

            <Button 
              className="w-full mb-6" 
              size="lg"
              onClick={() => {
                trackEvent('pricing_plan_clicked', { plan: 'premium', billing: selectedBilling });
                navigate('/auth');
              }}
            >
              Upgrade to Premium
            </Button>

            <ul className="space-y-3">
              {premiumFeatures.map((feature, idx) => (
                <li key={idx} className="flex items-start gap-3">
                  {feature.includes("Everything in") ? (
                    <span className="text-sm font-semibold text-foreground">{feature}</span>
                  ) : (
                    <>
                      <Check className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-muted-foreground">{feature}</span>
                    </>
                  )}
                </li>
              ))}
            </ul>
          </Card>
        </div>

        {/* Early adopter + Student note */}
        <div className="text-center mt-8 space-y-2">
          <p className="text-sm text-muted-foreground">
            🔒 Early adopters get locked-in pricing. Start free, upgrade anytime.
          </p>
          <p className="text-sm text-muted-foreground">
            🎓 Student? Use code <span className="font-mono font-bold text-primary">STUDENT30</span> at checkout for 30% off your first 3 months.
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section className="container mx-auto px-4 py-16 pb-24">
        <h2 className="text-2xl md:text-3xl font-bold text-foreground text-center mb-12">
          Frequently Asked Questions
        </h2>

        <div className="max-w-3xl mx-auto space-y-6">
          <Card className="p-6">
            <h3 className="text-lg font-semibold text-foreground mb-2">
              Can I upgrade or downgrade anytime?
            </h3>
            <p className="text-muted-foreground">
              Yes! Upgrade to Premium anytime from your profile. Downgrade at the end of your billing cycle. No cancellation fees.
            </p>
          </Card>

          <Card className="p-6">
            <h3 className="text-lg font-semibold text-foreground mb-2">
              What happens to my data if I cancel Premium?
            </h3>
            <p className="text-muted-foreground">
              All your study history, streaks, and XP remain intact. You'll lose access to Premium features but keep all your progress.
            </p>
          </Card>

          <Card className="p-6">
            <h3 className="text-lg font-semibold text-foreground mb-2">
              How do I use the student discount?
            </h3>
            <p className="text-muted-foreground">
              Enter code <span className="font-mono font-bold">STUDENT30</span> at checkout to get 30% off your first 3 months. Works with any billing plan.
            </p>
          </Card>

          <Card className="p-6">
            <h3 className="text-lg font-semibold text-foreground mb-2">
              What payment methods do you accept?
            </h3>
            <p className="text-muted-foreground">
              We accept all major credit cards and debit cards via Stripe. Your payment information is encrypted and never stored on our servers.
            </p>
          </Card>

          <Card className="p-6">
            <h3 className="text-lg font-semibold text-foreground mb-2">
              Why should I choose Annual?
            </h3>
            <p className="text-muted-foreground">
              Annual saves you 37% compared to monthly — that's like getting 4+ months free. Plus, you commit to your study goals for the year.
            </p>
          </Card>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-secondary/50 py-16">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-foreground mb-4">
            Ready for Consistent Study You'll Actually Enjoy?
          </h2>
          <p className="text-muted-foreground mb-8 max-w-2xl mx-auto">
            Join students building serious study habits with StudyForge.
          </p>
          <Button size="lg" onClick={() => {
            trackEvent('footer_cta_clicked', { page: 'pricing' });
            navigate('/auth');
          }}>
            Get Started Free
          </Button>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
};

export default Pricing;
